import {
  idiomFontSize,
  TYPE,
  resolveLayout,
  type Rect,
} from "@gaoya/shared";
import { isMyTurn, remainingMatchMs, type UiState } from "../../game/state.js";
import { opponentHudLayout } from "../../ui/hall.js";
import { C, clipName, drawCircleAvatar, drawPanel, drawTag, fillRound, roundPath, wrapLines } from "../theme.js";
import { drawCupRow, drawHintPouches, itemColumnRects } from "../widgets/cups.js";
import { drawAnswerCountdown } from "../widgets/incense.js";
import { drawKeyboardSkin, waitingOpponentLabel } from "../widgets/keyboard-skin.js";
import { drawScroll } from "../widgets/scroll.js";

export type BattleBands = {
  layout: ReturnType<typeof resolveLayout>;
  selfPot: Rect;
  ident: Rect;
  abbr: Rect;
  cand: Rect;
  keys: Rect;
  keyboard: Rect;
  scroll: Rect;
};

export function battleBands(
  w: number,
  h: number,
  ui?: Pick<UiState, "candidates" | "safeBottom" | "safeTop" | "capsuleTop" | "capsuleHeight" | "mode" | "peers">,
): BattleBands {
  const layout = resolveLayout(w, h);
  if (ui) {
    const need = opponentHudLayout(w, ui).barH;
    const extra = Math.max(0, need - layout.opponentBar.h);
    if (extra > 0) {
      layout.opponentBar.h = need;
      layout.opponentPot.y = need;
      layout.opponentPot.h = 4;
      layout.idiomStage.y = need + 4;
      layout.idiomStage.h = Math.max(88, layout.wordScroll.y - layout.idiomStage.y);
    }
  }
  const self = layout.selfPot;
  const kb = layout.keyboard;
  const bottomPad = Math.max(8, ui?.safeBottom ?? 0);
  const insetX = 12;
  const insetTop = 14;
  const keys = {
    x: kb.x + insetX,
    y: kb.y + insetTop,
    w: kb.w - insetX * 2,
    h: Math.max(110, kb.h - bottomPad - insetTop - 6),
  };
  const abbrH = Math.max(26, Math.min(32, self.h * 0.28));
  const candH = ui && ui.candidates.length > 0 ? Math.max(28, Math.min(34, self.h * 0.3)) : 0;
  const identH = Math.max(36, self.h - abbrH - candH);
  return {
    layout,
    selfPot: self,
    ident: { x: self.x, y: self.y, w: self.w, h: identH },
    cand: {
      x: self.x + 8,
      y: self.y + identH,
      w: self.w - 16 - (ui && ui.mode !== "RUSH" ? 84 : 0),
      h: candH,
    },
    abbr: { x: self.x + 10, y: self.y + identH + candH, w: self.w - 20, h: abbrH },
    keys,
    keyboard: kb,
    scroll: layout.wordScroll,
  };
}

export function drawBattle(ctx: CanvasRenderingContext2D, ui: UiState, w: number, h: number, now: number): void {
  const bands = battleBands(w, h, ui);
  const L = bands.layout;
  drawBattleHud(ctx, ui, L.opponentBar, now);
  drawIdiomStage(ctx, ui, L.idiomStage, now);
  drawScroll(ctx, ui, L.wordScroll);
  const items = ui.mode === "RUSH" ? null : itemColumnRects(bands.ident);
  const identBottom = items ? items.hint.y + items.hint.h : bands.ident.y + bands.ident.h;
  const selfScored =
    ui.mode === "RUSH" && !!ui.scorePop && now < ui.scorePop.until && ui.scorePop.playerId === ui.userId;
  drawPanel(
    ctx,
    bands.selfPot.x + 12,
    bands.ident.y + 2,
    bands.selfPot.w - 24,
    Math.max(bands.ident.h - 4, identBottom - bands.ident.y),
    14,
    selfScored ? C.goldSoft : C.card,
    selfScored ? C.gold : C.line,
  );
  drawSelfRow(ctx, ui, bands.ident, now);
  drawAbbr(ctx, ui, bands.abbr);
  drawKeyboardSkin(ctx, ui, bands.keyboard, bands.keys, now);
}

function drawBattleHud(ctx: CanvasRenderingContext2D, ui: UiState, r: Rect, now: number): void {
  const hud = opponentHudLayout(r.w, ui);
  const rush = ui.mode === "RUSH";
  ctx.font = `600 12px ${TYPE.sans}`;
  const pillText = `第 ${ui.round || 0} 回合 · ${rush ? "抢答" : "1v1"}`;
  const pillW = Math.min(hud.roundPill.w, ctx.measureText(pillText).width + 34);
  fillRound(ctx, hud.roundPill.x, hud.roundPill.y, pillW, hud.roundPill.h - 2, 13, "rgba(255,252,245,0.85)");
  fillRound(ctx, hud.roundPill.x + 11, hud.roundPill.y + 10, 6, 6, 3, C.seal);
  ctx.fillStyle = C.ink;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const modeLabel = rush ? "抢答" : "1v1";
  ctx.fillText(
    clipName(ctx, `第 ${ui.round || 0} 回合 · ${modeLabel}`, hud.roundPill.w - 28),
    hud.roundPill.x + 20,
    hud.roundPill.y + hud.roundPill.h / 2,
  );

  if (rush) {
    const ms = remainingMatchMs(ui, now);
    const sec = Math.ceil(ms / 1000);
    const mm = Math.floor(sec / 60);
    const ss = String(sec % 60).padStart(2, "0");
    fillRound(ctx, hud.clock.x, hud.clock.y, hud.clock.w, hud.clock.h - 2, 13, sec <= 30 ? C.seal : C.night);
    ctx.fillStyle = "#FFF7EE";
    ctx.font = `700 15px ${TYPE.sans}`;
    ctx.textAlign = "center";
    ctx.fillText(`${mm}:${ss}`, hud.clock.x + hud.clock.w / 2, hud.clock.y + hud.clock.h / 2);
  }

  const cards = rush ? ui.peers.slice(0, hud.cards.length) : ui.opponent ? [ui.opponent] : [];
  for (let i = 0; i < hud.cards.length; i++) {
    const card = hud.cards[i];
    if (!card) continue;
    const peer = cards[i];
    drawOppCard(ctx, ui, card, peer, now, rush);
  }
}

function drawOppCard(
  ctx: CanvasRenderingContext2D,
  ui: UiState,
  card: Rect,
  peer: { id: string; nickname: string } | null | undefined,
  now: number,
  rush: boolean,
): void {
  const turn = !rush && !!peer && ui.currentTurnId === peer.id;
  const scored = !!peer && !!ui.scorePop && now < ui.scorePop.until && ui.scorePop.playerId === peer.id;
  drawPanel(ctx, card.x, card.y, card.w, card.h, 14, scored ? C.goldSoft : C.card, scored || turn ? C.gold : C.line);
  const name = peer?.nickname ?? "茶客";
  const img = peer ? (peer.id === ui.opponent?.id ? ui.oppAvatarImg : ui.peerImgs[peer.id] ?? ui.oppAvatarImg) : null;
  const cy = card.y + card.h / 2;
  const avR = rush ? 14 : 17;
  drawCircleAvatar(ctx, img, card.x + 12 + avR, cy, avR, name);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const textX = card.x + 12 + avR * 2 + 10;
  if (rush && peer) {
    const textW = card.w - (textX - card.x) - 8;
    ctx.fillStyle = C.ink;
    ctx.font = `600 13px ${TYPE.sans}`;
    ctx.fillText(clipName(ctx, name, textW), textX, cy - 9);
    ctx.fillStyle = C.seal;
    ctx.font = `700 15px ${TYPE.serif}`;
    ctx.fillText(`${ui.scores[peer.id] ?? 0}`, textX, cy + 10);
    const sw = ctx.measureText(`${ui.scores[peer.id] ?? 0}`).width;
    ctx.fillStyle = C.ink2;
    ctx.font = `11px ${TYPE.sans}`;
    ctx.fillText("分", textX + sw + 3, cy + 11);
    if (scored) {
      ctx.fillStyle = C.seal;
      ctx.font = `700 13px ${TYPE.sans}`;
      ctx.fillText("+1", textX + sw + 18, cy + 10);
    }
    return;
  }
  const cupsW = 112;
  ctx.fillStyle = C.ink;
  ctx.font = `700 14px ${TYPE.sans}`;
  ctx.fillText(clipName(ctx, name, card.w - (textX - card.x) - cupsW - 12), textX, cy - 8);
  ctx.fillStyle = C.ink3;
  ctx.font = `11px ${TYPE.sans}`;
  let status = turn ? "码字中…" : "对席";
  if (peer && !ui.opponentOnline && ui.opponentGraceDeadline > 0) {
    const left = Math.max(0, Math.ceil((ui.opponentGraceDeadline - (now + ui.clockOffset)) / 1000));
    status = `离席 ${left}s`;
    ctx.fillStyle = C.seal;
  } else if (turn && (ui.oppScramble?.turnsLeft ?? 0) > 0) {
    status = `活字已乱 · ${ui.oppScramble?.turnsLeft}手`;
    ctx.fillStyle = C.seal;
  } else if (turn) ctx.fillStyle = C.goldDeep;
  ctx.fillText(status, textX, cy + 11);
  drawCupRow(ctx, { x: card.x + card.w - cupsW - 10, y: card.y + 6, w: cupsW, h: card.h - 12 }, ui.oppHp, now, 0.62);
}

function drawCopybookCell(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, tail: boolean): void {
  fillRound(ctx, x, y + 2, size, size, 10, tail ? "rgba(168,122,44,0.25)" : "rgba(92,62,32,0.08)");
  fillRound(ctx, x, y, size, size, 10, tail ? C.goldSoft : C.card);
  const ink = tail ? C.goldDeep : C.seal;
  ctx.save();
  ctx.strokeStyle = ink;
  ctx.globalAlpha = tail ? 0.35 : 0.22;
  ctx.lineWidth = 1;
  roundPath(ctx, x + 0.5, y + 0.5, size - 1, size - 1, 10);
  ctx.stroke();
  ctx.setLineDash([3, 3]);
  const inset = 6;
  const cx = x + size / 2;
  const cy = y + size / 2;
  ctx.beginPath();
  ctx.moveTo(x + inset, cy);
  ctx.lineTo(x + size - inset, cy);
  ctx.moveTo(cx, y + inset);
  ctx.lineTo(cx, y + size - inset);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

function drawIdiomStage(ctx: CanvasRenderingContext2D, ui: UiState, r: Rect, now: number): void {
  drawPanel(ctx, r.x + 12, r.y + 4, r.w - 24, r.h - 8, 18, "rgba(255,252,245,0.94)");
  const word = ui.lastWord || "……";
  const chars = [...word];
  const flash = ui.lastActionFlash && now < ui.lastActionFlash.until;
  const n = Math.max(chars.length, 4);
  const footer = 58;
  const size = Math.min(
    Math.max(40, idiomFontSize(r.h, r.w)),
    r.h - footer - 20,
    (r.w - 56) / n - 6,
  );
  const gap = 8;
  const total = n * size + (n - 1) * gap;
  const x0 = r.x + (r.w - total) / 2;
  const y0 = r.y + Math.max(12, (r.h - footer - size) / 2);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let i = 0; i < n; i++) {
    const ch = chars[i] ?? "";
    const x = x0 + i * (size + gap);
    const tail = i === n - 1 && !!ch;
    drawCopybookCell(ctx, x, y0, size, tail);
    ctx.fillStyle =
      flash && ui.lastActionFlash ? (ui.lastActionFlash.ok ? C.jade : C.seal) : tail ? C.seal : C.ink;
    ctx.font = `700 ${Math.floor(size * 0.58)}px ${TYPE.serif}`;
    ctx.fillText(ch, x + size / 2, y0 + size / 2 + 1);
  }
  const py = ui.lastPinyins.join(" / ") || "";
  const mine = isMyTurn(ui);
  const pyY = y0 + size + 16;
  ctx.textAlign = "left";
  ctx.fillStyle = C.ink2;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText("尾音接", r.x + 28, pyY);
  if (py) {
    ctx.fillStyle = C.seal;
    ctx.font = `700 14px ${TYPE.sans}`;
    ctx.fillText(py, r.x + 70, pyY);
  }
  const turn =
    ui.mode === "RUSH"
      ? !ui.selfRacing
        ? "观战"
        : "开抢！"
      : mine
        ? "轮到你"
        : waitingOpponentLabel(ui, now);
  const hot = (ui.mode === "RUSH" && ui.selfRacing) || mine;
  drawTag(ctx, turn, r.x + r.w - 28, pyY - 11, 22, hot ? C.seal : C.well, hot ? "#FFFFFF" : C.ink2, "right", 12);

  drawAnswerCountdown(ctx, ui, { x: r.x + 28, y: pyY + 14, w: r.w - 56, h: 30 }, now);

  if (now < ui.bannerUntil && ui.banner) {
    ctx.font = `600 13px ${TYPE.sans}`;
    const innerMax = Math.max(80, r.w - 48);
    const lines = wrapLines(ctx, ui.banner, innerMax);
    let textW = 0;
    for (const line of lines) textW = Math.max(textW, ctx.measureText(line).width);
    const bw = Math.min(r.w - 24, Math.max(120, textW + 28));
    const lineH = 18;
    const bh = Math.max(26, 8 + lines.length * lineH);
    const bx = r.x + (r.w - bw) / 2;
    const by = r.y - 4;
    fillRound(ctx, bx, by, bw, bh, 13, "rgba(42,33,27,0.92)");
    ctx.fillStyle = "#FFF7EE";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const ty0 = by + bh / 2 - ((lines.length - 1) * lineH) / 2;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i] ?? "", r.x + r.w / 2, ty0 + i * lineH);
    }
  }
}

function drawSelfRow(ctx: CanvasRenderingContext2D, ui: UiState, r: Rect, now: number): void {
  const avR = 17;
  const cy = r.y + r.h / 2;
  drawCircleAvatar(ctx, ui.avatarImg, r.x + 26 + avR, cy, avR, ui.nickname);
  const tx = r.x + 26 + avR * 2 + 10;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  if (ui.mode === "RUSH") {
    const scored = !!ui.scorePop && now < ui.scorePop.until && ui.scorePop.playerId === ui.userId;
    ctx.fillStyle = C.ink;
    ctx.font = `700 14px ${TYPE.sans}`;
    const name = clipName(ctx, ui.nickname, r.w - 200);
    ctx.fillText(name, tx, cy - 9);
    if (!ui.selfRacing) {
      const nw = ctx.measureText(name).width;
      ctx.fillStyle = C.ink2;
      ctx.font = `600 11px ${TYPE.sans}`;
      ctx.fillText("观战", tx + nw + 8, cy - 9);
    }
    ctx.fillStyle = C.seal;
    ctx.font = `700 18px ${TYPE.serif}`;
    const sc = `${ui.scores[ui.userId] ?? 0}`;
    ctx.fillText(sc, tx, cy + 11);
    const sw = ctx.measureText(sc).width;
    ctx.fillStyle = C.ink2;
    ctx.font = `12px ${TYPE.sans}`;
    ctx.fillText("分", tx + sw + 3, cy + 12);
    if (scored) {
      ctx.fillStyle = C.seal;
      ctx.font = `700 14px ${TYPE.sans}`;
      ctx.fillText("+1", tx + sw + 20, cy + 11);
    }
    return;
  }
  ctx.fillStyle = C.ink;
  ctx.font = `700 14px ${TYPE.sans}`;
  ctx.fillText(clipName(ctx, ui.nickname, r.w - 250), tx, cy - 9);
  drawTag(ctx, "我", tx, cy + 2, 18, C.sealSoft, C.seal, "left", 10);
  const cupsW = 112;
  const hint = 72 + 12 + 8;
  drawCupRow(ctx, { x: r.x + r.w - hint - cupsW - 4, y: r.y + 6, w: cupsW, h: r.h - 12 }, ui.selfHp, now, 0.62);
  drawHintPouches(ctx, r, ui.hintsLeft, ui.scrambleItemsLeft);
}

function drawAbbr(ctx: CanvasRenderingContext2D, ui: UiState, r: Rect): void {
  const raw = ui.abbr.toUpperCase();
  const slots = 4;
  const boxH = Math.min(r.h - 6, 28);
  const boxW = Math.min(34, boxH + 6);
  const gap = 6;
  const start = r.x + 4;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let i = 0; i < slots; i++) {
    const x = start + i * (boxW + gap);
    const y = r.y + (r.h - boxH) / 2;
    const ch = raw[i] ?? "";
    const active = i === raw.length;
    drawPanel(ctx, x, y, boxW, boxH, 8, ch ? C.card : "rgba(255,252,245,0.6)", active ? C.seal : C.line, null);
    ctx.fillStyle = ch ? C.seal : C.ink3;
    ctx.font = `700 ${Math.min(17, boxH - 8)}px ${TYPE.sans}`;
    ctx.fillText(ch || "·", x + boxW / 2, y + boxH / 2 + 1);
  }
  ctx.fillStyle = C.ink2;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.textAlign = "left";
  const labelX = start + slots * (boxW + gap) + 6;
  ctx.fillText(raw.length >= 4 ? "点选候选成语，或按「提交」" : "输入四字声母，如 rsrh", labelX, r.y + r.h / 2);
}
